import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio email di reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="forgot-back-link"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#5C5B5A] hover:text-[#1C1B1A] mb-8"
        >
          <ArrowLeft size={14} /> Torna al login
        </Link>

        <div className="border border-[#E5E4E0] bg-white p-8 md:p-10">
          <div className="mb-6">
            <div className="w-12 h-12 border border-[#2C4C3B] rounded-full flex items-center justify-center mb-4">
              {sent ? <MailCheck size={20} className="text-[#2C4C3B]" /> : <KeyRound size={20} className="text-[#2C4C3B]" />}
            </div>
            <div className="label-mono">Recupero accesso</div>
            <h1 className="font-display text-3xl mt-2 font-light">
              {sent ? "Controlla la tua email" : "Password dimenticata?"}
            </h1>
          </div>

          {sent ? (
            <div className="space-y-4 text-sm text-[#1C1B1A] leading-relaxed" data-testid="forgot-sent-message">
              <p>
                Se l&apos;email <span className="font-mono">{email}</span> è registrata, tra pochi minuti
                riceverai un messaggio con il link per reimpostare la password.
              </p>
              <p className="text-xs text-[#5C5B5A]">
                Il link è valido per <strong>1 ora</strong>. Se non arriva, controlla la cartella Spam.
              </p>
              <p className="text-xs text-[#5C5B5A]">
                ⚠ L&apos;email viene inviata attraverso il tuo account SMTP configurato in Segretaria AI:
                se nessun account email è collegato o funzionante, il reset non può essere completato
                (in tal caso contatta il supporto Emergent).
              </p>
              <div className="pt-4">
                <Link to="/login">
                  <Button
                    data-testid="forgot-return-login"
                    className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase tracking-[0.2em] text-xs py-6"
                  >
                    Torna al Login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <p className="text-sm text-[#5C5B5A] leading-relaxed">
                Inserisci l&apos;email con cui ti sei registrato. Ti invieremo un link per creare
                una nuova password (valido per 1 ora).
              </p>
              <div>
                <Label className="label-mono">Email</Label>
                <Input
                  required
                  type="email"
                  autoFocus
                  data-testid="forgot-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tuoemail@esempio.com"
                  className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-button"
                className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase tracking-[0.2em] text-xs py-6"
              >
                {loading ? "Invio in corso…" : "Invia link di reset"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
